import { useState } from 'react';
import { cadastrarPessoa, darSenhaProvisoria, excluirConta } from '../../acesso';
import type { FuncaoDaEquipe } from '../../acesso';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { mensagemDeFalha, useEscape } from './Equipe';

/**
 * EQUIPE — cadastrar, senha PROVISÓRIA e excluir (dono, 29/09: "senha provisória, aí o usuário
 * entra e cria a permanente"; excluir = desativar e esconder). O admin nunca sabe a senha
 * definitiva: a provisória vale só até o primeiro acesso.
 */

function Modal({
  titulo,
  sub,
  perigo = false,
  enviando,
  aoCancelar,
  children,
}: {
  titulo: string;
  sub: string;
  perigo?: boolean;
  enviando: boolean;
  aoCancelar: () => void;
  children: React.ReactNode;
}) {
  useEscape(aoCancelar, !enviando);
  return (
    <div
      className="ac-veu ac-veu-centro"
      onClick={(e) => {
        e.stopPropagation();
        if (!enviando) aoCancelar();
      }}
      role="presentation"
    >
      <div
        className={`ac-modal${perigo ? ' ac-modal-perigo' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">{titulo}</div>
          <div className="ac-modal-sub">{sub}</div>
        </div>
        <div className="ac-modal-corpo">{children}</div>
      </div>
    </div>
  );
}

function Erro({ texto }: { texto: string | null }) {
  if (!texto) return null;
  return (
    <div className="ac-erro" role="alert" style={{ marginBottom: 12 }}>
      <div className="ac-erro-titulo">Não foi possível salvar</div>
      <div className="ac-erro-texto">{texto} Nada foi alterado.</div>
    </div>
  );
}

const AVISO_DA_PROVISORIA =
  'É uma senha PROVISÓRIA: no primeiro acesso a pessoa é obrigada a criar a senha dela. Passe a provisória pessoalmente — você nunca fica sabendo a definitiva.';

export function CadastrarPessoa({
  sessao,
  aoCancelar,
  aoCadastrar,
}: {
  sessao: Sessao;
  aoCancelar: () => void;
  aoCadastrar: () => void;
}) {
  const [nome, setNome] = useState('');
  const [login, setLogin] = useState('');
  const [funcao, setFuncao] = useState<FuncaoDaEquipe>('balconista');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const pronto = nome.trim().length >= 2 && login.trim().length >= 1 && senha.length >= 6;
  return (
    <Modal
      titulo="Cadastrar pessoa"
      sub="Cria o acesso ao InovaPay com senha provisória."
      enviando={enviando}
      aoCancelar={aoCancelar}
    >
      <label className="eq-campo">
        <span>Nome completo</span>
        <input value={nome} onChange={(e) => setNome(e.target.value)} autoFocus maxLength={120} />
      </label>
      <div className="eq-dupla">
        <label className="eq-campo">
          <span>Código de acesso</span>
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value.trim())}
            inputMode="numeric"
            maxLength={40}
            placeholder="ex.: 321"
          />
        </label>
        <label className="eq-campo">
          <span>Função</span>
          <select value={funcao} onChange={(e) => setFuncao(e.target.value as FuncaoDaEquipe)}>
            <option value="balconista">Balconista</option>
            <option value="admin_geral">Administrador</option>
            <option value="auditor">Auditor</option>
          </select>
        </label>
      </div>
      <label className="eq-campo">
        <span>Senha provisória (mínimo 6 caracteres)</span>
        <input
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoComplete="off"
          maxLength={200}
        />
      </label>
      <div className="ac-info" style={{ marginBottom: 12 }}>
        <Icone nome="key" tamanho={15} traco={1.7} />
        <div>{AVISO_DA_PROVISORIA}</div>
      </div>
      <Erro texto={erro} />
      <div className="ac-botoes-modal">
        <button
          type="button"
          className="ac-botao-cheio"
          disabled={enviando || !pronto}
          onClick={() => {
            setEnviando(true);
            setErro(null);
            void cadastrarPessoa(sessao, {
              nome: nome.trim(),
              login: login.trim(),
              senhaProvisoria: senha,
              funcao,
            }).then((r) => {
              setEnviando(false);
              if (r.ok) aoCadastrar();
              else setErro(mensagemDeFalha(r.falha));
            });
          }}
        >
          {enviando ? 'Cadastrando…' : 'Cadastrar'}
        </button>
        <button
          type="button"
          className="ac-botao-contorno"
          onClick={aoCancelar}
          disabled={enviando}
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
}

export function SenhaProvisoria({
  sessao,
  accountId,
  nome,
  aoCancelar,
  aoFeito,
}: {
  sessao: Sessao;
  accountId: string;
  nome: string;
  aoCancelar: () => void;
  aoFeito: () => void;
}) {
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  return (
    <Modal
      titulo={`Senha provisória para ${nome}`}
      sub="Para quem esqueceu a senha."
      enviando={enviando}
      aoCancelar={aoCancelar}
    >
      <label className="eq-campo">
        <span>Senha provisória (mínimo 6 caracteres)</span>
        <input
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoFocus
          autoComplete="off"
          maxLength={200}
        />
      </label>
      <div className="ac-info" style={{ marginBottom: 12 }}>
        <Icone nome="key" tamanho={15} traco={1.7} />
        <div>{AVISO_DA_PROVISORIA} As sessões abertas desta pessoa caem na hora.</div>
      </div>
      <Erro texto={erro} />
      <div className="ac-botoes-modal">
        <button
          type="button"
          className="ac-botao-cheio"
          disabled={enviando || senha.length < 6}
          onClick={() => {
            setEnviando(true);
            setErro(null);
            void darSenhaProvisoria(sessao, accountId, senha).then((r) => {
              setEnviando(false);
              if (r.ok) aoFeito();
              else setErro(mensagemDeFalha(r.falha));
            });
          }}
        >
          {enviando ? 'Salvando…' : 'Dar senha provisória'}
        </button>
        <button
          type="button"
          className="ac-botao-contorno"
          onClick={aoCancelar}
          disabled={enviando}
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
}

export function ExcluirPessoa({
  sessao,
  accountId,
  nome,
  aoCancelar,
  aoFeito,
}: {
  sessao: Sessao;
  accountId: string;
  nome: string;
  aoCancelar: () => void;
  aoFeito: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  return (
    <Modal
      titulo={`Excluir ${nome}`}
      sub="O acesso acaba na hora e a pessoa some da lista."
      perigo
      enviando={enviando}
      aoCancelar={aoCancelar}
    >
      <div className="ac-efeitos">
        <div className="ac-efeitos-titulo">O QUE ACONTECE</div>
        <div className="ac-efeito ac-efeito-nao">
          <Icone nome="x" tamanho={14} traco={1.9} />
          <span>Perde o acesso ao InovaPay agora (as sessões caem)</span>
        </div>
        <div className="ac-efeito ac-efeito-sim">
          <Icone nome="check" tamanho={14} traco={2.2} />
          <span>
            Vendas, Giro, premiações e auditorias <b>continuam</b> com o nome dela
          </span>
        </div>
        <div className="ac-efeito ac-efeito-sim">
          <Icone nome="check" tamanho={14} traco={2.2} />
          <span>Fica no filtro "Excluídos" — dá para reativar se precisar</span>
        </div>
      </div>
      <div className="ac-efeitos-titulo">MOTIVO</div>
      <textarea
        className="ac-texto-area"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder="Ex.: saiu da farmácia"
        maxLength={500}
        autoFocus
      />
      <Erro texto={erro} />
      <div className="ac-botoes-modal">
        <button
          type="button"
          className="ac-botao-cheio"
          disabled={enviando || motivo.trim().length < 5}
          onClick={() => {
            setEnviando(true);
            setErro(null);
            void excluirConta(sessao, accountId, motivo.trim()).then((r) => {
              setEnviando(false);
              if (r.ok) aoFeito();
              else setErro(mensagemDeFalha(r.falha));
            });
          }}
        >
          {enviando ? 'Excluindo…' : 'Confirmar exclusão'}
        </button>
        <button
          type="button"
          className="ac-botao-contorno"
          onClick={aoCancelar}
          disabled={enviando}
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
}
